import React, { useRef } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Button, Dropdown, Typography, message } from "@mimber/ui-web"
import {
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  SendOutlined,
  StopOutlined,
  DeleteOutlined,
} from "@mimber/ui-web"
import {
  useCurrentOrganization,
  useGenerateDonationPagePreviewToken,
} from "@mimber/client/mimber-organization"
import { usePermissionedMenuItems } from "@/core/hooks/usePermissionedMenuItems"
import type { PermissionedMenuItem } from "@/core/hooks/usePermissionedMenuItems/usePermissionedMenuItems.types"
import {
  DeleteDonationPageModal,
  PublishDonationPageModal,
  UnpublishDonationPageModal,
} from "@/components/modals"
import { Resource, Action } from "@mimber/schema"
import type { DonationPageActionsDropdownProps } from "./DonationPageActionsDropdown.types"

const { Text } = Typography

export const DonationPageActionsDropdown: React.FunctionComponent<
  DonationPageActionsDropdownProps
> = ({
  donationPage,
  onActionComplete,
  onDeleteSuccess,
  size = "small",
  type = "text",
  iconOnly = true,
}) => {
  const navigate = useNavigate()

  const { data: currentOrgResponse } = useCurrentOrganization()
  const organization = currentOrgResponse?.data?.organization || null
  const generateToken = useGenerateDonationPagePreviewToken(donationPage.uuid)

  const openPublishRef = useRef<(() => void) | null>(null)
  const openUnpublishRef = useRef<(() => void) | null>(null)
  const openDeleteRef = useRef<(() => void) | null>(null)

  const handleView = () => {
    if (!organization?.slug) {
      message.error("Organization must have a slug to view the public page")
      return
    }

    generateToken.mutate(undefined, {
      onSuccess: (response) => {
        const token = response.data.previewToken
        const orgSlug = organization.slug
        const mimberAppUrl =
          import.meta.env.VITE_MIMBER_APP_URL || window.location.origin
        const previewUrl = `${mimberAppUrl}/org/${orgSlug}/donate/${donationPage.slug}?previewToken=${token}`
        window.open(previewUrl, "_blank", "noopener,noreferrer")
        message.info("Public page opened in new tab")
      },
      onError: (error) => {
        message.error(error.message || "Failed to open public page")
      },
    })
  }

  const menuItems: PermissionedMenuItem[] = [
    {
      key: "view",
      label: "View",
      icon: <EyeOutlined />,
      resource: Resource.DONATION,
      action: Action.READ,
      onClick: handleView,
    },
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      resource: Resource.DONATION,
      action: Action.UPDATE,
      onClick: () =>
        navigate({
          to: "/finance/donations/pages/$pageUuid/edit",
          params: { pageUuid: donationPage.uuid },
        }),
    },
    ...(donationPage.isPublished
      ? ([
          {
            key: "unpublish",
            label: "Unpublish",
            icon: <StopOutlined />,
            resource: Resource.DONATION,
            action: Action.UPDATE,
            onClick: () => openUnpublishRef.current?.(),
          },
        ] as PermissionedMenuItem[])
      : ([
          {
            key: "publish",
            label: "Publish",
            icon: <SendOutlined />,
            resource: Resource.DONATION,
            action: Action.UPDATE,
            onClick: () => openPublishRef.current?.(),
          },
        ] as PermissionedMenuItem[])),
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      resource: Resource.DONATION,
      action: Action.DELETE,
      onClick: () => openDeleteRef.current?.(),
    },
  ]

  const { items } = usePermissionedMenuItems({ items: menuItems })

  if (items.length === 0) {
    return <Text type="secondary">—</Text>
  }

  return (
    <>
      <PublishDonationPageModal
        donationPage={donationPage}
        onSuccess={onActionComplete}
        renderButton={(open) => {
          openPublishRef.current = open
          return <></>
        }}
      />
      <UnpublishDonationPageModal
        donationPage={donationPage}
        onSuccess={onActionComplete}
        renderButton={(open) => {
          openUnpublishRef.current = open
          return <></>
        }}
      />
      <DeleteDonationPageModal
        donationPage={donationPage}
        onSuccess={() => {
          onActionComplete?.()
          onDeleteSuccess?.()
        }}
        renderButton={(open) => {
          openDeleteRef.current = open
          return <></>
        }}
      />
      <Dropdown menu={{ items }} trigger={["click"]}>
        <Button type={type} size={size} icon={<MoreOutlined />}>
          {!iconOnly && "Actions"}
        </Button>
      </Dropdown>
    </>
  )
}
